import { useCallback, useLayoutEffect, useState } from 'react';

export interface Dimensions {
  x: number;
  y: number;
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

const emptyDimensions: Dimensions = {
  x: 0,
  y: 0,
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
  width: 0,
  height: 0,
};

export function useDimensions(): {
  ref: (node: HTMLElement | null) => void;
  dimensions: Dimensions;
} {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const ref = useCallback((newNode: HTMLElement | null) => {
    setNode(newNode);
  }, []);
  const [dimensions, setDimensions] = useState<Dimensions>(emptyDimensions);

  const measure = useCallback((element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    setDimensions({
      x: rect.left,
      y: rect.top,
      left: rect.left,
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    });
  }, []);

  useLayoutEffect(() => {
    if (!node) {
      return;
    }

    measure(node);

    const resizeObserver = new ResizeObserver(() => {
      measure(node);
    });

    resizeObserver.observe(node);

    return () => {
      resizeObserver.disconnect();
    };
  }, [measure, node]);

  return {
    dimensions,
    ref,
  };
}
